import { useState } from 'react';
import toast from 'react-hot-toast';
import { useClauses } from '../../hooks/useClauses';
import { useConfirm } from '../../hooks/useConfirm';
import { clauseService } from '../../services/clauseService';
import { ClauseList } from './ClauseList';
import { ClauseEditor } from './ClauseEditor';
import type { Clause } from '@shared/types';

export function ClauseManager() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [approvedFilter, setApprovedFilter] = useState<boolean | undefined>(undefined);
  const [selectedClause, setSelectedClause] = useState<Clause | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { clauses, loading, reload } = useClauses({
    search,
    type: typeFilter || undefined,
    approved: approvedFilter,
  });

  const { confirm, ConfirmDialog } = useConfirm();

  const handleCreateNew = () => {
    setSelectedClause(null);
    setIsCreating(true);
  };

  const handleEdit = (clause: Clause) => {
    setSelectedClause(clause);
    setIsCreating(false);
  };

  const handleSave = async (clause: Partial<Clause>) => {
    const toastId = toast.loading(selectedClause ? 'Updating clause...' : 'Creating clause...');
    
    try {
      if (selectedClause) {
        await clauseService.updateClause(selectedClause.id, clause);
        toast.success('Clause updated successfully!', { id: toastId });
      } else {
        await clauseService.createClause(clause);
        toast.success('Clause created successfully!', { id: toastId });
      }
      setSelectedClause(null);
      setIsCreating(false);
      reload();
    } catch (error) {
      console.error('Failed to save clause:', error);
      toast.error('Failed to save clause.', { id: toastId });
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Clause',
      message: 'Are you sure you want to delete this clause? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (!confirmed) return;
    
    const toastId = toast.loading('Deleting clause...');
    
    try {
      await clauseService.deleteClause(id);
      toast.success('Clause deleted successfully!', { id: toastId });
      reload();
    } catch (error) {
      console.error('Failed to delete clause:', error);
      toast.error('Failed to delete clause.', { id: toastId });
    }
  };

  const handleCancel = () => {
    setSelectedClause(null);
    setIsCreating(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Clause Library</h1>
          <p className="mt-2 text-gray-600">
            Manage reusable contract clauses and terms
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Clause List */}
          <div>
            <ClauseList
              clauses={clauses}
              loading={loading}
              search={search}
              onSearchChange={setSearch}
              typeFilter={typeFilter}
              onTypeFilterChange={setTypeFilter}
              approvedFilter={approvedFilter}
              onApprovedFilterChange={setApprovedFilter}
              onCreateNew={handleCreateNew}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </div>

          {/* Clause Editor */}
          <div>
            {(selectedClause || isCreating) && (
              <ClauseEditor
                clause={selectedClause}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            )}
            {!selectedClause && !isCreating && (
              <div className="bg-white shadow-sm rounded-lg p-8 text-center text-gray-500">
                Select a clause to edit or create a new one
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog />
    </div>
  );
}

